import { Component, Element, Host, Prop, h } from '@stencil/core';
import warn from '../../capabilities/warn';

/**
 * @description Applies styles to a button tag, used to submit forms and invoke
 * actions. Not to be confused with the Call to Action (CTA) component.
 * @use
 * @since 5.8.0
 * @slot - `default slot`: The button element.
 * @version figma-v1
 */
@Component({
  tag: 'hp6-button-decorator',
  styleUrl: 'button-decorator.scss',
  shadow: false, // Used by other components inside their shadow DOM, and already satisfies the single-tag policy without shadow DOM
})
export class ButtonDecorator {
  /**
   * Activate isInverted style of child button
   */
  @Prop({ reflect: true }) isInverted: boolean = false;

  /**
   * Set to to prevent wrapping of button text
   */
  @Prop({ reflect: true }) noWrap: boolean = false;

  /**
   * The padding and font size of the child button
   */
  @Prop({ reflect: true }) sizeSelection: 'sm' | 'md' = 'md';

  /**
   * The type of button. Determines the style of the button.
   */
  @Prop({ reflect: true }) typeSelection: 'primary' | 'secondary' | 'action' | 'tertiary'
    = 'primary';

  /**
   * A reference to the <Host /> element
   */
  @Element() host: HTMLElement;

  /**
   * Stencil.js lifecycle
   */
  componentDidLoad() {
    this.decorate();
  }

  /**
   * Stencil.js lifecycle
   */
  componentDidRender() {
    this.decorate();
  }

  /**
   * Gets the <button /> element inside the <slot />
   */
  get button(): HTMLButtonElement {
    const button = this.host.querySelector('button');
    if (!button) {
      warn('hp6-button-decorator: no button found');
    }

    return button;
  }

  /**
   * Decorate and style the <button />
   */
  private decorate() {
    const { button, isInverted, sizeSelection, typeSelection, noWrap } = this;

    // remove all classes that might have been added in a previous render
    const classList = [
      'inverted',
      'no-wrap',
      'primary',
      'secondary',
      'action',
      'tertiary',
      'sm',
      'md',
    ];
    button.classList.remove(...classList);

    button.classList.add('hp-button-style');

    if (isInverted) {
      button.classList.add('inverted');
    }

    if (noWrap) {
      button.classList.add('no-wrap');
    }

    button.classList.add(typeSelection);
    button.classList.add(sizeSelection);

    // icons are required to xs size
    const icon: HTMLHp6IconElement = button.querySelector('hp6-icon');

    if (icon) {
      // TODO: change prop name icon.size to icon.sizeSelection
      icon.sizeSelection = 'xs';
    }
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    return (
      <Host>
        <slot />
      </Host>
    );
  }
}
